import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, GraduationCap, Award, BookOpen, Users, Calendar, ArrowRight, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TopperSliderSection = () => {
  const [currentCourseIndex, setCurrentCourseIndex] = useState(0);
  const [toppers, setToppers] = useState([]);
  const [topperStartIndex, setTopperStartIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Ongoing Batches dataset matching reference design
  const courses = [
    {
      title: "UPSC Program",
      headerBg: "bg-[#134E4A]",
      items: [
        { text: "UPSC Foundation Course", link: "/courses#upsc-foundation" },
        { text: "Integrated Batch", link: "/courses#upsc-integrated" },
        { text: "Personalized Mentorship", link: "/courses#personalized-mentorship" },
        { text: "5-Month Answer Writing", link: "/courses#answer-writing" },
        { text: "Essay Writing", link: "/courses#essay-writing" },
        { text: "Interview Guidance", link: "/courses#interview-guidance" },
        { text: "Current Affairs Program", link: "/courses#current-affairs" }
      ]
    },
    {
      title: "CAPF Program",
      headerBg: "bg-[#0F3D39]",
      items: [
        { text: "CAPF AC Comprehensive Mentorship", link: "/courses#capf-mentorship" },
        { text: "CAPF Paper I & Paper II Intensive Batch", link: "/courses#capf-mentorship" },
        { text: "CAPF Test Series & Answer Evaluation", link: "/courses#capf-mentorship" },
        { text: "CAPF Interview Guidance", link: "/courses#capf-mentorship" }
      ]
    },
    {
      title: "MPSC Program",
      headerBg: "bg-[#0B302D]",
      items: [
        { text: "MPSC Integrated Comprehensive Batch", link: "/courses#mpsc-foundation" },
        { text: "MPSC वैयक्तिक मेंटरशिप प्रोग्रॅम", link: "/courses#mpsc-personalized-mentorship" },
        { text: "MPSC उत्तरलेखन प्रोग्रॅम (५ महिने)", link: "/courses#mpsc-answer-writing" },
        { text: "एथिक्स कोर्स – MPSC स्पेश", link: "/courses#ethics-mentorship" }
      ]
    }
  ];

  // Fallback Toppers Dataset matching reference mockup exact ratios
  const fallbackToppers = [
    {
      id: "f1",
      name: "Saurav Dhakne",
      rank: "AIR 6, UPSC CSE 2021",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/saurav_dhakane.webp"
    },
    {
      id: "f2",
      name: "Pallavi Sangle",
      rank: "AIR 45, UPSC CSE 2021",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/pallavi.webp"
    },
    {
      id: "f3",
      name: "Dayanand Tendolkar",
      rank: "AIR 421, CSE 2021",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/dayanand.webp"
    },
    {
      id: "f4",
      name: "Air 421 CSE 2021",
      rank: "UPSC Topper",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/ajinkya.jpg"
    },
    {
      id: "f5",
      name: "Ankita Verma",
      rank: "AIR 67, UPSC CSE 2022",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/rushikesh_gaikwad.webp"
    },
    {
      id: "f6",
      name: "Shreyas Kadam",
      rank: "AIR 54, CAPF AC 2024",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/shreyas_kadam.webp"
    },
    {
      id: "f7",
      name: "Kiran Shinde",
      rank: "AIR 288, CAPF AC 2024",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/kiran_shinde.webp"
    },
    {
      id: "f8",
      name: "Rutik Yadav",
      rank: "AIR 383, CAPF AC 2024",
      badge: "AIR Topper",
      category: "UPSC / CAPF",
      image: "/images/toppers/rutik_yadav.webp"
    }
  ];

  const WP_API_URL = 'https://dimensionias.com/cms/wp-json/wp/v2';

  const fetchToppers = async () => {
    try {
      setLoading(true);
      const categoriesResponse = await fetch(`${WP_API_URL}/categories?slug=toppers`);
      if (!categoriesResponse.ok) throw new Error('Failed to fetch categories');
      const categories = await categoriesResponse.json();

      if (!categories || categories.length === 0) throw new Error('No category found');

      const toppersCategory = categories[0];
      const response = await fetch(`${WP_API_URL}/posts?categories=${toppersCategory.id}&_embed&per_page=100&status=publish`);
      if (!response.ok) throw new Error('Failed to fetch posts');
      const data = await response.json();

      const transformedToppers = data.map(post => {
        const rawTitle = post.title?.rendered ? post.title.rendered.replace(/<[^>]*>/g, '').trim() : '';
        const rawExcerpt = post.excerpt?.rendered ? post.excerpt.rendered.replace(/<[^>]*>/g, '').trim() : '';

        let name = post.acf?.topper_name || rawTitle;
        let rank = post.acf?.rank;
        let category = post.acf?.exam_category;

        // Smart Name & Rank cleanup if title contains rank text (e.g. "SHREYAS KADAM CAPF AC2024")
        if (name) {
          const matchRankInName = name.match(/^(.*?)\s+(CAPF|AIR\s*\d+|UPSC|CSE\s*\d+)(.*)$/i);
          if (matchRankInName) {
            if (!rank) {
              rank = (matchRankInName[2] + ' ' + matchRankInName[3]).trim();
            }
            name = matchRankInName[1].trim();
          }
        }

        if (!rank && rawExcerpt) {
          const match = rawExcerpt.match(/(AIR\s*\d+[^,]*(?:,\s*UPSC|\s*CSE|\s*\d{4})*)/i);
          if (match) rank = match[1];
        }

        let image = post.acf?.topper_image?.url ||
          post._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
          post.meta?.featured_image_url ||
          post.meta?.fi_url;

        if (!image && post.content?.rendered) {
          const imgMatch = post.content.rendered.match(/<img[^>]+src=["']([^"']+)["']/i);
          if (imgMatch) image = imgMatch[1];
        }

        if (!image) image = '/images/default-topper.jpg';

        return {
          id: post.id,
          name: name || 'Topper Name',
          rank: rank || 'AIR Topper',
          badge: 'AIR Topper',
          category: category || 'UPSC / CAPF',
          image: image,
          order: post.acf?.display_order || 0
        };
      });

      if (transformedToppers.length > 0) {
        setToppers(transformedToppers);
      } else {
        setToppers(fallbackToppers);
      }
    } catch (err) {
      setToppers(fallbackToppers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToppers();
  }, []);

  // Autoplay Toppers Slider
  useEffect(() => {
    if (toppers.length === 0) return;
    const interval = setInterval(() => {
      setTopperStartIndex((prev) => (prev + 1) % toppers.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [toppers]);

  const handleNextTopper = () => {
    setTopperStartIndex((prev) => (prev + 1) % toppers.length);
  };

  const handlePrevTopper = () => {
    setTopperStartIndex((prev) => (prev - 1 + toppers.length) % toppers.length);
  };

  const visibleToppers = () => {
    if (toppers.length === 0) return [];
    const result = [];
    for (let i = 0; i < 5; i++) {
      result.push(toppers[(topperStartIndex + i) % toppers.length]);
    }
    return result;
  };

  return (
    <div className="relative w-full bg-[#F8FAF9] pt-2 pb-8 overflow-hidden">

      {/* ----------------- HERO BACKGROUND PHOTO ----------------- */}
      <div className="absolute top-0 left-0 right-0 h-[380px] sm:h-[420px] lg:h-[440px] pointer-events-none z-0 overflow-hidden">
        <img
          src="/images/hero/parliament_hero_bg.png"
          alt="Parliament Architecture Background"
          className="w-full h-full object-cover object-center opacity-85 scale-105"
        />
        {/* Soft subtle bottom fade into page background */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(248,250,249,0.1) 0%, rgba(248,250,249,0.25) 60%, #F8FAF9 100%)'
          }}
        ></div>
      </div>

      {/* ----------------- SECTION 1: HERO MAIN BANNER & ONGOING BATCHES ----------------- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-stretch gap-4 my-1">

          {/* LEFT COLUMN: HERO HEADLINE & FEATURES */}
          <div className="flex-1 bg-white/45 backdrop-blur-[3px] rounded-2xl p-3.5 sm:p-5 border border-white/80 shadow-sm flex flex-col justify-between relative overflow-hidden">

            <div>
              {/* Top Sub-badge Tag */}
              <div className="inline-block bg-emerald-50 text-[#134E4A] text-[11px] font-extrabold px-3 py-0.5 rounded-full border border-emerald-200/80 mb-2 shadow-sm">
                India's Trusted UPSC Coaching Institute
              </div>

              {/* Main Heading */}
              <h1 className="text-2xl sm:text-3xl lg:text-3xl xl:text-4xl font-black text-slate-900 leading-snug tracking-tight mb-2">
                Your Journey to <span className="text-[#134E4A]">Success</span> Starts Here
              </h1>

              {/* Subheading */}
              <p className="text-slate-600 font-semibold text-xs sm:text-sm mb-3">
                Expert Guidance. Proven Strategy. Personalized Mentorship.
              </p>

              {/* 4 Feature Cards (Horizontal Row) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-2">
                <div className="bg-white p-2 rounded-lg border border-slate-200/90 shadow-sm flex items-center gap-2 transition-transform hover:-translate-y-0.5">
                  <div className="p-1.5 rounded-md bg-emerald-50 text-[#134E4A] shrink-0">
                    <Users size={16} />
                  </div>
                  <span className="text-[11px] font-bold text-slate-800 leading-tight">Experienced Faculty</span>
                </div>

                <div className="bg-white p-2 rounded-lg border border-slate-200/90 shadow-sm flex items-center gap-2 transition-transform hover:-translate-y-0.5">
                  <div className="p-1.5 rounded-md bg-emerald-50 text-[#134E4A] shrink-0">
                    <Award size={16} />
                  </div>
                  <span className="text-[11px] font-bold text-slate-800 leading-tight">Proven Results</span>
                </div>

                <div className="bg-white p-2 rounded-lg border border-slate-200/90 shadow-sm flex items-center gap-2 transition-transform hover:-translate-y-0.5">
                  <div className="p-1.5 rounded-md bg-emerald-50 text-[#134E4A] shrink-0">
                    <BookOpen size={16} />
                  </div>
                  <span className="text-[11px] font-bold text-slate-800 leading-tight">Comprehensive Material</span>
                </div>

                <div className="bg-white p-2 rounded-lg border border-slate-200/90 shadow-sm flex items-center gap-2 transition-transform hover:-translate-y-0.5">
                  <div className="p-1.5 rounded-md bg-emerald-50 text-[#134E4A] shrink-0">
                    <GraduationCap size={16} />
                  </div>
                  <span className="text-[11px] font-bold text-slate-800 leading-tight">1-on-1 Mentorship</span>
                </div>
              </div>
            </div>

            {/* CTA Buttons Row */}
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <button
                onClick={() => navigate('/courses')}
                className="bg-[#134E4A] text-white font-extrabold text-xs px-5 py-2.5 rounded-lg shadow-sm hover:bg-[#0F3D39] transition-all flex items-center gap-1.5 transform hover:-translate-y-0.5"
              >
                <span>Explore Courses</span>
                <ArrowRight size={14} />
              </button>

              <button
                onClick={() => navigate('/contact')}
                className="bg-white border-2 border-[#134E4A] text-[#134E4A] font-extrabold text-xs px-5 py-2.5 rounded-lg hover:bg-emerald-50 transition-all flex items-center gap-1.5 shadow-sm transform hover:-translate-y-0.5"
              >
                <Calendar size={14} />
                <span>Schedule Mentorship</span>
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: ONGOING BATCHES & PROGRAMS CARD */}
          <div className="w-full lg:w-[310px] shrink-0 bg-white rounded-2xl p-3.5 sm:p-4 shadow-md border border-slate-200/80 flex flex-col justify-between">
            <div>
              {/* Header */}
              <div className="flex items-center gap-2 text-slate-900 font-black text-xs uppercase tracking-wider mb-2.5 pb-1.5 border-b border-slate-100">
                <Calendar size={16} className="text-[#134E4A]" />
                <span>ONGOING BATCHES & PROGRAMS</span>
              </div>

              {/* Program Selector Header Banner */}
              <div className="bg-[#134E4A] text-white font-extrabold text-xs py-2 px-3 rounded-lg shadow-sm mb-2 flex items-center justify-between">
                <span>{courses[currentCourseIndex].title}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentCourseIndex((prev) => (prev - 1 + courses.length) % courses.length)}
                    className="p-0.5 hover:bg-white/20 rounded transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={() => setCurrentCourseIndex((prev) => (prev + 1) % courses.length)}
                    className="p-0.5 hover:bg-white/20 rounded transition-colors"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {/* Program Items List */}
              <div className="space-y-1">
                {courses[currentCourseIndex].items.map((item, idx) => (
                  <a
                    key={idx}
                    href={item.link}
                    className="group flex items-center justify-between bg-slate-50 hover:bg-emerald-50/90 p-1.5 px-2 rounded-lg transition-all duration-150 border border-slate-100/80"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#134E4A] shrink-0"></span>
                      <span className="text-[11px] font-bold text-slate-800 group-hover:text-[#134E4A] transition-colors line-clamp-1">
                        {item.text}
                      </span>
                    </div>
                    <ChevronRight size={12} className="text-slate-400 group-hover:text-[#134E4A] shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Bottom Action Button */}
            <button
              onClick={() => navigate('/courses')}
              className="w-full bg-[#134E4A] text-white font-bold text-xs py-2 rounded-lg shadow hover:bg-[#0F3D39] transition-all text-center mt-2.5"
            >
              View All Programs
            </button>
          </div>

        </div>
      </div>

      {/* ----------------- SECTION 2: OUR STAR PERFORMERS & TOPPERS CONTAINER ----------------- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <div className="bg-[#134E4A] rounded-2xl p-4 sm:p-5 shadow-xl relative overflow-hidden border border-emerald-800/60 text-white">

          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-emerald-800/80 pb-3 mb-4 gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xl sm:text-2xl">🏆</span>
              <div>
                <h3 className="text-lg sm:text-xl font-black tracking-wide text-white">
                  Our Star Performers & Toppers
                </h3>
                <p className="text-[11px] text-emerald-200 font-medium">Their Success, Our Pride</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-block text-[10px] font-black uppercase tracking-widest bg-emerald-900/90 text-emerald-200 px-3 py-0.5 rounded-full border border-emerald-700/80 shadow-inner">
                PROVEN RESULTS
              </span>

              {/* Navigation Arrows */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handlePrevTopper}
                  className="w-7 h-7 rounded-full bg-emerald-900/80 hover:bg-emerald-800 text-white flex items-center justify-center transition-colors border border-emerald-700 shadow-sm"
                  title="Previous Topper"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={handleNextTopper}
                  className="w-7 h-7 rounded-full bg-emerald-900/80 hover:bg-emerald-800 text-white flex items-center justify-center transition-colors border border-emerald-700 shadow-sm"
                  title="Next Topper"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Topper Cards Responsive Grid (5 Cards Visible on Desktop, Portraits Dominate 180-200px) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 items-stretch">
            {visibleToppers().map((topper, idx) => (
              <div
                key={topper.id || idx}
                className="bg-white rounded-xl p-2.5 text-slate-900 flex flex-col items-center text-center shadow border border-slate-100 hover:scale-105 transition-all duration-300 group"
              >
                {/* Photo with Gold Ring - PORTRAIT DOMINATES CARD (180-200px) */}
                <div className="relative mb-2 mt-0.5 flex justify-center items-center">
                  <div className="w-40 h-40 sm:w-44 sm:h-44 md:w-48 md:h-48 lg:w-44 lg:h-44 xl:w-48 xl:h-48 rounded-full p-1 bg-gradient-to-tr from-amber-500 via-amber-300 to-amber-600 shadow-lg flex items-center justify-center relative transition-transform duration-300 group-hover:scale-105">
                    <div className="w-full h-full rounded-full p-0.5 bg-white flex items-center justify-center overflow-hidden shadow-inner">
                      <img
                        src={topper.image}
                        alt={topper.name}
                        loading="lazy"
                        className="w-full h-full rounded-full object-cover object-[center_top] transform transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/default-topper.jpg';
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Name */}
                <h4 className="text-xs font-black text-slate-900 line-clamp-1 mb-0.5 group-hover:text-[#134E4A] transition-colors">
                  {topper.name}
                </h4>

                {/* Rank */}
                <p className="text-[11px] font-bold text-[#134E4A] mb-1 leading-tight">
                  {topper.rank}
                </p>

                {/* Badge Tag */}
                <span className="inline-block bg-[#134E4A] text-white text-[9px] font-black uppercase px-2.5 py-0.5 rounded-full mb-0.5 shadow-sm">
                  {topper.badge}
                </span>

                {/* Exam Subtext */}
                <span className="text-[9px] font-bold text-slate-500">
                  {topper.category}
                </span>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* ----------------- SLEEK COMPACT STICKY SIDE ACTION BUTTONS ----------------- */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 pointer-events-auto">
        <button
          onClick={() => navigate('/contact')}
          className="bg-[#134E4A] text-white px-1.5 py-3 rounded-l-xl shadow-lg hover:bg-[#0F3D39] transition-all flex flex-col items-center gap-1.5 border-l border-y border-emerald-400/30 text-[9px] font-extrabold tracking-wider uppercase group hover:-translate-x-1"
          title="Schedule Mentorship"
        >
          <Calendar size={14} className="group-hover:scale-110 transition-transform text-white shrink-0" />
          <span className="hidden sm:inline writing-mode-vertical text-[9px] font-bold text-emerald-100">Mentorship</span>
        </button>

        <a
          href="https://wa.me/917530807530"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#00A884] text-white px-1.5 py-3 rounded-l-xl shadow-lg hover:bg-[#008f70] transition-all flex flex-col items-center gap-1.5 border-l border-y border-green-300/30 text-[9px] font-extrabold tracking-wider uppercase group hover:-translate-x-1"
          title="Chat on WhatsApp"
        >
          <MessageCircle size={14} className="group-hover:scale-110 transition-transform text-white shrink-0" />
          <span className="hidden sm:inline writing-mode-vertical text-[9px] font-bold text-green-100">WhatsApp</span>
        </a>
      </div>

    </div>
  );
};

export default TopperSliderSection;